"use client";

import { useState, useCallback, useRef } from "react";

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Hook that measures a container's size using ResizeObserver.
 * Returns a ref callback to attach to the container and the current size.
 *
 * Uses a callback ref so the ResizeObserver re-attaches whenever the
 * underlying DOM node changes (e.g. a loading placeholder replaced by
 * the real content).
 *
 * @example
 * ```tsx
 * const { containerRef, containerSize } = useContainerSize<HTMLDivElement>();
 *
 * return (
 *   <div ref={containerRef}>
 *     {containerSize && <p>Width: {containerSize.width}</p>}
 *   </div>
 * );
 * ```
 */
export function useContainerSize<T extends HTMLElement = HTMLDivElement>() {
  const [containerSize, setContainerSize] = useState<ContainerSize | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((node: T | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    // Absent in SSR and jsdom-based tests — leave the size as null so
    // consumers fall back to their unmeasured rendering.
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return { containerRef, containerSize };
}
